import { z } from 'zod'
import { judgeInvitationStatusSchema } from './judge-invitation'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const judgeProfileRequestSchema = z.strictObject({
  organization: z.string().max(200).nullable().default(null),
  title: z.string().max(200).nullable().default(null),
  bio: z.string().max(4000).nullable().default(null),
  expertise: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
})
export type JudgeProfileRequest = z.infer<typeof judgeProfileRequestSchema>
export const judgeProfileDtoSchema = judgeProfileRequestSchema.extend({
  userId: publicIdSchema,
  displayName: z.string(),
  email: z.string().email(),
  workload: z.int().min(0),
})
export type JudgeProfileDto = z.infer<typeof judgeProfileDtoSchema>
export const createJudgeAssignmentRequestSchema = z.strictObject({
  judgeUserId: publicIdSchema,
  submissionId: publicIdSchema,
  rubricVersionId: publicIdSchema,
})
export type CreateJudgeAssignmentRequest = z.infer<typeof createJudgeAssignmentRequestSchema>
export const judgeAssignmentDtoSchema = createJudgeAssignmentRequestSchema.extend({
  id: publicIdSchema,
  roundId: publicIdSchema,
  status: z.enum(['active', 'cancelled', 'completed']),
  createdAt: utcTimestampSchema,
})
export type JudgeAssignmentDto = z.infer<typeof judgeAssignmentDtoSchema>
export const adminJudgeAssignmentDtoSchema = judgeAssignmentDtoSchema.extend({
  judgeDisplayName: z.string(),
  judgeEmail: z.string().email(),
  teamName: z.string(),
  categoryName: z.string(),
  rubricVersionNumber: z.int().min(1),
  rubricVersionLabel: z.string().nullable(),
  /** PR-317: the work's team has been disqualified, so this assignment will not be counted. */
  teamDisqualified: z.boolean(),
})
export type AdminJudgeAssignmentDto = z.infer<typeof adminJudgeAssignmentDtoSchema>

/** ISSUE-PREVIEW #14: assigns every (submission x judge) pair to one rubric version in one request instead of one dropdown submit at a time. */
export const batchCreateJudgeAssignmentRequestSchema = z.strictObject({
  submissionIds: z.array(publicIdSchema).min(1).max(200),
  judgeUserIds: z.array(publicIdSchema).min(1).max(200),
  rubricVersionId: publicIdSchema,
})
export type BatchCreateJudgeAssignmentRequest = z.infer<
  typeof batchCreateJudgeAssignmentRequestSchema
>
export const batchAssignmentSkipReasonSchema = z.enum([
  'duplicate',
  'conflict_of_interest',
  'not_a_judge',
  'team_disqualified',
])
export const batchCreateJudgeAssignmentResultSchema = z.object({
  created: z.array(judgeAssignmentDtoSchema),
  skipped: z.array(
    z.object({
      submissionId: publicIdSchema,
      judgeUserId: publicIdSchema,
      reason: batchAssignmentSkipReasonSchema,
    }),
  ),
})
export type BatchCreateJudgeAssignmentResult = z.infer<
  typeof batchCreateJudgeAssignmentResultSchema
>

/**
 * ISSUE-PREVIEW #15b: reassign a judge and/or swap the rubric version on an
 * existing, still-active assignment instead of only ever being able to
 * create new ones.
 *
 * `status: 'cancelled'` is a second, mutually exclusive mode added for the
 * Judging Load / Conflict-of-Interest overlap alert's "adjust assignment"
 * action: it cancels the assignment instead of reassigning it, and may not
 * be combined with `judgeUserId`/`rubricVersionId` in the same call. This is
 * deliberately the only status transition this endpoint accepts from the
 * outside — `judge_assignments.status` otherwise only ever moves to
 * `'completed'` as a side effect of the judge submitting a score.
 */
export const updateJudgeAssignmentRequestSchema = z
  .strictObject({
    judgeUserId: publicIdSchema.optional(),
    rubricVersionId: publicIdSchema.optional(),
    status: z.literal('cancelled').optional(),
    reason: z.string().trim().min(1).max(2000).optional(),
  })
  .refine(
    (v) => v.judgeUserId !== undefined || v.rubricVersionId !== undefined || v.status !== undefined,
    'At least one of judgeUserId, rubricVersionId, or status must be provided.',
  )
  .refine(
    (v) =>
      v.status === undefined || (v.judgeUserId === undefined && v.rubricVersionId === undefined),
    'status may not be combined with judgeUserId or rubricVersionId.',
  )
export type UpdateJudgeAssignmentRequest = z.infer<typeof updateJudgeAssignmentRequestSchema>
/** PR-317: cancels every still-active assignment on a disqualified team's work in one step. */
export const cancelDisqualifiedAssignmentsRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export type CancelDisqualifiedAssignmentsRequest = z.infer<
  typeof cancelDisqualifiedAssignmentsRequestSchema
>
export const createJudgeConflictRequestSchema = z
  .strictObject({
    judgeUserId: publicIdSchema.optional(),
    submissionId: publicIdSchema.nullable().default(null),
    teamId: publicIdSchema.nullable().default(null),
    reason: z.string().trim().min(1).max(2000),
  })
  .refine((v) => v.submissionId !== null || v.teamId !== null, 'A submission or team is required.')
export type CreateJudgeConflictRequest = z.infer<typeof createJudgeConflictRequestSchema>
export const judgeConflictSourceSchema = z.enum(['self_declared', 'admin_defined'])
export const judgeConflictDtoSchema = z.strictObject({
  id: publicIdSchema,
  judgeUserId: publicIdSchema,
  submissionId: publicIdSchema.nullable(),
  teamId: publicIdSchema.nullable(),
  teamName: z.string().nullable(),
  reason: z.string(),
  source: judgeConflictSourceSchema,
  createdAt: utcTimestampSchema,
})
export type JudgeConflictDto = z.infer<typeof judgeConflictDtoSchema>
/**
 * SDD v1.1 §4.4.4 / §13.3 (Load Alerts: "Conflict-related reassignment
 * required"): `overlappingAssignmentId` is populated when this judge holds
 * an `active` `judge_assignments` row for the same submission (or, for a
 * team-scoped conflict, any submission belonging to that team) as this
 * declared conflict — a conflict should already have excluded the
 * assignment, so a non-null value here is a genuine data-integrity signal,
 * not a fabricated one. It is the assignment id the "adjust assignment"
 * action (`PATCH .../judges/assignments/:id` with `status: 'cancelled'`)
 * targets.
 */
export const adminJudgeConflictDtoSchema = judgeConflictDtoSchema.extend({
  judgeDisplayName: z.string(),
  overlappingAssignmentId: publicIdSchema.nullable(),
})
export type AdminJudgeConflictDto = z.infer<typeof adminJudgeConflictDtoSchema>

/**
 * SDD v1.1 §13 (Judging Operations Specification) Judge Table / Load Alerts.
 * Deliberately read-only aggregation — see the heuristic notes on
 * `JudgesRepository.loadTable` and the flag computation in
 * `apps/api/src/routes/judges.ts` for why `overloaded`/`under_assigned`/
 * `no_activity` are explainable operational hints, not a domain rule.
 */
export const judgeLoadFlagSchema = z.enum(['overloaded', 'under_assigned', 'no_activity'])
export type JudgeLoadFlag = z.infer<typeof judgeLoadFlagSchema>
export const judgeLoadRowDtoSchema = z.strictObject({
  judgeUserId: publicIdSchema,
  displayName: z.string(),
  email: z.string().email(),
  /** 'accepted' when the judge role was granted directly with no invitation record on file. */
  invitationStatus: judgeInvitationStatusSchema,
  /** Active + completed judge_assignments rows (cancelled rows are excluded). */
  assignmentsAssigned: z.int().min(0),
  assignmentsCompleted: z.int().min(0),
  /** Active, not-yet-completed judge_assignments rows. */
  assignmentsPending: z.int().min(0),
  conflictsCount: z.int().min(0),
  lastActivityAt: utcTimestampSchema.nullable(),
  flags: z.array(judgeLoadFlagSchema),
})
export type JudgeLoadRowDto = z.infer<typeof judgeLoadRowDtoSchema>
export const judgeLoadTableDtoSchema = z.strictObject({
  generatedAt: utcTimestampSchema,
  meanActiveAssignments: z.number().min(0),
  overloadedMultiplier: z.number().min(1),
  noActivityWindowDays: z.int().min(1),
  rows: z.array(judgeLoadRowDtoSchema),
})
export type JudgeLoadTableDto = z.infer<typeof judgeLoadTableDtoSchema>

/**
 * Judge reminders: who still has scoring to do, and the email that would be
 * sent. Counts exclude conflict-excluded assignments. Carries no addresses.
 */
export const judgeReminderRecipientDtoSchema = z.strictObject({
  judgeUserId: publicIdSchema,
  displayName: z.string(),
  pendingCount: z.number().int().min(1),
  totalCount: z.number().int().min(1),
  nextDeadline: z.string().nullable(),
})
export type JudgeReminderRecipientDto = z.infer<typeof judgeReminderRecipientDtoSchema>
export const judgeReminderPreviewDtoSchema = z.strictObject({
  subject: z.string(),
  text: z.string(),
  recipientCount: z.number().int().min(1),
  recipients: z.array(judgeReminderRecipientDtoSchema),
})
export type JudgeReminderPreviewDto = z.infer<typeof judgeReminderPreviewDtoSchema>
export const judgeReminderResultDtoSchema = z.strictObject({
  recipientCount: z.number().int().min(0),
  userIds: z.array(publicIdSchema),
  failedUserIds: z.array(publicIdSchema),
})
export type JudgeReminderResultDto = z.infer<typeof judgeReminderResultDtoSchema>

/** A judge who cannot serve withdraws; the reason is mandatory and goes to the audit trail. */
export const judgeWithdrawalRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
  /** Also give up the judge role in this competition (the organizer must invite again); default keeps it. */
  leaveRole: z.boolean().default(false),
})
export type JudgeWithdrawalRequest = z.infer<typeof judgeWithdrawalRequestSchema>
export const judgeWithdrawalResultDtoSchema = z.strictObject({
  cancelledCount: z.number().int().min(0),
  leftRole: z.boolean(),
})
export type JudgeWithdrawalResultDto = z.infer<typeof judgeWithdrawalResultDtoSchema>

/** Opt-in automatic reminder before a round's judging deadline. */
export const judgeAutoReminderSettingsRequestSchema = z.strictObject({
  enabled: z.boolean(),
  hoursBefore: z.int().min(1).max(336),
})
export type JudgeAutoReminderSettingsRequest = z.infer<
  typeof judgeAutoReminderSettingsRequestSchema
>
export const judgeAutoReminderSettingsDtoSchema = z.strictObject({
  enabled: z.boolean(),
  hoursBefore: z.int().min(1).max(336),
  /** Rounds whose automatic reminder already went out, newest first. */
  sent: z.array(
    z.strictObject({
      roundId: publicIdSchema,
      sentAt: z.string(),
      recipientCount: z.number().int().min(0),
    }),
  ),
})
export type JudgeAutoReminderSettingsDto = z.infer<typeof judgeAutoReminderSettingsDtoSchema>

/**
 * Automatic, load-balanced assignment: the organizer picks a round, a rubric
 * version and how many judges each submission needs; the server proposes
 * (`apply: false`) or creates (`apply: true`) the assignments, skipping
 * conflicts and existing pairs.
 */
export const autoDistributeRequestSchema = z.strictObject({
  roundId: publicIdSchema,
  rubricVersionId: publicIdSchema,
  judgesPerSubmission: z.int().min(1).max(10),
  /** Restrict to these judges; omitted means every judge in the competition. */
  judgeUserIds: z.array(publicIdSchema).min(1).max(200).optional(),
  apply: z.boolean().default(false),
})
export type AutoDistributeRequest = z.infer<typeof autoDistributeRequestSchema>
export const autoDistributeResultDtoSchema = z.strictObject({
  applied: z.boolean(),
  eligibleSubmissions: z.int().min(0),
  plannedCount: z.int().min(0),
  createdCount: z.int().min(0),
  perJudge: z.array(
    z.strictObject({
      judgeUserId: publicIdSchema,
      displayName: z.string(),
      currentLoad: z.int().min(0),
      added: z.int().min(0),
    }),
  ),
  underCovered: z.array(z.strictObject({ submissionId: publicIdSchema, missing: z.int().min(1) })),
  skipped: z.array(
    z.strictObject({
      submissionId: publicIdSchema,
      judgeUserId: publicIdSchema,
      reason: batchAssignmentSkipReasonSchema,
    }),
  ),
})
export type AutoDistributeResultDto = z.infer<typeof autoDistributeResultDtoSchema>
